import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  Calendar,
  Clock,
  CheckCircle2,
  Circle,
  PlayCircle,
  ExternalLink,
  RefreshCw,
  ListTodo,
  CalendarDays,
  CalendarRange,
  Loader2,
  ClipboardList,
  ChevronRight
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Status icons and colors
const STATUS_CONFIG = {
  open: {
    icon: Circle,
    color: 'bg-slate-100 text-slate-600 hover:bg-slate-200',
    label: 'Open',
    badgeColor: 'bg-slate-100 text-slate-700'
  },
  bezig: {
    icon: PlayCircle,
    color: 'bg-blue-100 text-blue-600 hover:bg-blue-200',
    label: 'Bezig',
    badgeColor: 'bg-blue-100 text-blue-700'
  },
  klaar: {
    icon: CheckCircle2,
    color: 'bg-green-100 text-green-600 hover:bg-green-200',
    label: 'Klaar',
    badgeColor: 'bg-green-100 text-green-700'
  }
};

// Frequency badge colors
const FREQ_CONFIG = {
  dagelijks: { color: 'bg-amber-100 text-amber-700', label: 'Dagelijks' },
  wekelijks: { color: 'bg-purple-100 text-purple-700', label: 'Wekelijks' },
  maandelijks: { color: 'bg-teal-100 text-teal-700', label: 'Maandelijks' }
};

const Werkprogramma = () => {
  const { selectedLocatie: locatieId, selectedLocatieData } = useOutletContext();
  const locatieNaam = selectedLocatieData?.naam || '';
  
  const [searchParams, setSearchParams] = useSearchParams();
  const [view, setView] = useState(searchParams.get('view') || 'vandaag');
  const [taken, setTaken] = useState([]);
  const [stats, setStats] = useState({ total: 0, klaar: 0, bezig: 0, open: 0, progress: 0 });
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);

  // Fetch tasks
  const fetchTaken = useCallback(async () => {
    if (!locatieId) return;
    
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/locaties/${locatieId}/taken?view=${view}`);
      if (response.ok) {
        const data = await response.json();
        const fetchedTaken = data.taken || [];
        
        // Auto-generate tasks if none exist
        if (fetchedTaken.length === 0) {
          const periode = view === 'vandaag' ? 'vandaag' : view === 'week' ? 'week' : 'maand';
          const genResponse = await fetch(`${API_BASE}/api/locaties/${locatieId}/taken/genereer?periode=${periode}`, {
            method: 'POST'
          });
          
          if (genResponse.ok) {
            // Fetch again after generating
            const refreshResponse = await fetch(`${API_BASE}/api/locaties/${locatieId}/taken?view=${view}`);
            if (refreshResponse.ok) {
              const refreshData = await refreshResponse.json();
              setTaken(refreshData.taken || []);
              setStats(refreshData.stats || { total: 0, klaar: 0, bezig: 0, open: 0, progress: 0 });
              setDateRange(refreshData.dateRange || { start: '', end: '' });
              return;
            }
          }
        }
        
        setTaken(fetchedTaken);
        setStats(data.stats || { total: 0, klaar: 0, bezig: 0, open: 0, progress: 0 });
        setDateRange(data.dateRange || { start: '', end: '' });
      }
    } catch (error) {
      console.error('Error fetching taken:', error);
      toast.error('Fout bij laden van taken');
    } finally {
      setIsLoading(false);
    }
  }, [locatieId, view]);

  // Generate tasks
  const generateTaken = async () => {
    if (!locatieId) return;
    
    setIsGenerating(true);
    try {
      const periode = view === 'vandaag' ? 'vandaag' : view === 'week' ? 'week' : 'maand';
      const response = await fetch(`${API_BASE}/api/locaties/${locatieId}/taken/genereer?periode=${periode}`, {
        method: 'POST'
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`${data.details.created} nieuwe taken gegenereerd`);
        fetchTaken();
      } else {
        toast.error('Fout bij genereren van taken');
      }
    } catch (error) {
      console.error('Error generating taken:', error);
      toast.error('Fout bij genereren van taken');
    } finally {
      setIsGenerating(false);
    }
  };

  // Update task status
  const updateTaakStatus = async (taakId, newStatus) => {
    try {
      const response = await fetch(`${API_BASE}/api/locaties/${locatieId}/taken/${taakId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      
      if (response.ok) {
        // Update local state
        setTaken(prev => prev.map(t => 
          t._id === taakId ? { ...t, status: newStatus } : t
        ));
        
        // Update stats
        setStats(prev => {
          const oldTask = taken.find(t => t._id === taakId);
          const oldStatus = oldTask?.status || 'open';
          
          return {
            ...prev,
            [oldStatus]: prev[oldStatus] - 1,
            [newStatus]: prev[newStatus] + 1,
            progress: Math.round(((newStatus === 'klaar' ? prev.klaar + 1 : prev.klaar) / prev.total) * 100)
          };
        });
        
        const statusLabel = STATUS_CONFIG[newStatus].label;
        toast.success(`Taak gemarkeerd als ${statusLabel.toLowerCase()}`);
      }
    } catch (error) {
      console.error('Error updating task:', error);
      toast.error('Fout bij bijwerken van taak');
    }
  };

  // Cycle through status
  const cycleStatus = (taak) => {
    const statusOrder = ['open', 'bezig', 'klaar'];
    const currentIndex = statusOrder.indexOf(taak.status);
    const nextStatus = statusOrder[(currentIndex + 1) % statusOrder.length];
    updateTaakStatus(taak._id, nextStatus);
  };

  // Handle view change
  const handleViewChange = (newView) => {
    setView(newView);
    setSearchParams({ view: newView });
  };

  useEffect(() => {
    fetchTaken();
  }, [fetchTaken]);

  // Group tasks by date
  const groupedTaken = taken.reduce((acc, taak) => {
    const date = taak.datum;
    if (!acc[date]) {
      acc[date] = [];
    }
    acc[date].push(taak);
    return acc;
  }, {});

  // Format date for display
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    // 4.10: "vandaag" in Europe/Amsterdam — tussen 00:00 en 02:00 stond hier gisteren
    const vandaagNL = new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Amsterdam' });
    const morgenDt = new Date(`${vandaagNL}T12:00:00Z`);
    morgenDt.setUTCDate(morgenDt.getUTCDate() + 1);
    const morgenNL = morgenDt.toISOString().split('T')[0];

    if (dateStr === vandaagNL) {
      return 'Vandaag';
    } else if (dateStr === morgenNL) {
      return 'Morgen';
    } else {
      return date.toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' });
    }
  };

  if (!locatieId) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center text-slate-500">
          <ListTodo className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>Selecteer eerst een locatie</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Werkprogramma</h1>
          <p className="text-sm sm:text-base text-slate-500">{locatieNaam}</p>
        </div>
        <Button 
          onClick={generateTaken}
          disabled={isGenerating}
          className="bg-blue-600 hover:bg-blue-700 w-full sm:w-auto min-h-[44px]"
        >
          {isGenerating ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <RefreshCw className="w-4 h-4 mr-2" />
          )}
          Taken genereren
        </Button>
      </div>

      {/* Progress Card */}
      <Card>
        <CardContent className="pt-4 sm:pt-6 px-3 sm:px-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
            <div className="flex-1">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-slate-700">Voortgang</span>
                <span className="text-sm font-bold text-slate-900">{stats.progress}%</span>
              </div>
              <Progress value={stats.progress} className="h-3" />
            </div>
            <div className="flex justify-around sm:justify-start gap-4 sm:gap-6">
              <div className="text-center">
                <div className="text-xl sm:text-2xl font-bold text-emerald-600">{stats.klaar}</div>
                <div className="text-xs text-slate-500">Klaar</div>
              </div>
              <div className="text-center">
                <div className="text-xl sm:text-2xl font-bold text-blue-600">{stats.bezig}</div>
                <div className="text-xs text-slate-500">Bezig</div>
              </div>
              <div className="text-center">
                <div className="text-xl sm:text-2xl font-bold text-slate-400">{stats.open}</div>
                <div className="text-xs text-slate-500">Open</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* View Tabs */}
      <Tabs value={view} onValueChange={handleViewChange}>
        <TabsList className="grid w-full grid-cols-3 h-auto p-1">
          <TabsTrigger value="vandaag" className="flex items-center justify-center gap-1 sm:gap-2 py-3 sm:py-2 text-xs sm:text-sm min-h-[44px]">
            <Calendar className="w-4 h-4" />
            <span>Vandaag</span>
          </TabsTrigger>
          <TabsTrigger value="week" className="flex items-center justify-center gap-1 sm:gap-2 py-3 sm:py-2 text-xs sm:text-sm min-h-[44px]">
            <CalendarDays className="w-4 h-4" />
            <span>Week</span>
          </TabsTrigger>
          <TabsTrigger value="maand" className="flex items-center justify-center gap-1 sm:gap-2 py-3 sm:py-2 text-xs sm:text-sm min-h-[44px]">
            <CalendarRange className="w-4 h-4" />
            <span>Maand</span>
          </TabsTrigger>
        </TabsList>

        {/* Task Lists */}
        {['vandaag', 'week', 'maand'].map((tabView) => (
          <TabsContent key={tabView} value={tabView} className="mt-4 sm:mt-6">
            {isLoading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
              </div>
            ) : taken.length === 0 ? (
              <Card>
                <CardContent className="py-8 sm:py-12">
                  <div className="text-center text-slate-500">
                    <ClipboardList className="w-10 sm:w-12 h-10 sm:h-12 mx-auto mb-4 opacity-50" />
                    <p className="font-medium">Geen taken gevonden</p>
                    <p className="text-sm mt-1">Klik op &quot;Taken genereren&quot; om taken aan te maken</p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4 sm:space-y-6">
                {Object.entries(groupedTaken).sort().map(([date, dayTaken]) => (
                  <div key={date}>
                    <h3 className="text-xs sm:text-sm font-semibold text-slate-500 uppercase tracking-wide mb-2 sm:mb-3">
                      {formatDate(date)}
                    </h3>
                    <div className="space-y-2">
                      {dayTaken.map((taak) => (
                        <TaakItem 
                          key={taak._id} 
                          taak={taak} 
                          onStatusChange={cycleStatus}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};

// Task Item Component
const TaakItem = ({ taak, onStatusChange }) => {
  const statusConfig = STATUS_CONFIG[taak.status];
  const freqConfig = FREQ_CONFIG[taak.frequentie];
  const StatusIcon = statusConfig.icon;

  return (
    <Card className={`transition-all active:scale-[0.99] ${
      taak.status === 'klaar' ? 'bg-green-50 border-green-200' : 
      taak.status === 'bezig' ? 'bg-blue-50 border-blue-200' : ''
    }`}>
      <CardContent className="py-3 sm:py-4 px-3 sm:px-6">
        <div className="flex items-start sm:items-center gap-3 sm:gap-4">
          {/* Status Button - Large touch target */}
          <button
            onClick={() => onStatusChange(taak)}
            className={`p-3 sm:p-2 rounded-full transition-colors shrink-0 min-w-[44px] min-h-[44px] flex items-center justify-center ${statusConfig.color}`}
            title={`Status: ${statusConfig.label}. Tik om te wijzigen.`}
          >
            <StatusIcon className="w-5 h-5 sm:w-5 sm:h-5" />
          </button>

          {/* Task Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start sm:items-center gap-2 flex-wrap">
              <span className={`font-medium text-sm sm:text-base ${
                taak.status === 'klaar' ? 'text-green-700 line-through' : 'text-slate-900'
              }`}>
                {taak.titel}
              </span>
              {taak.isChecklist && (
                <Badge variant="outline" className="text-xs">
                  Checklist
                </Badge>
              )}
            </div>
            {taak.beschrijving && (
              <p className="text-xs sm:text-sm text-slate-500 mt-1 line-clamp-2 sm:truncate">
                {taak.beschrijving}
              </p>
            )}
            <div className="flex items-center gap-2 sm:gap-3 mt-2 flex-wrap">
              {taak.tijd && (
                <span className="flex items-center text-xs text-slate-400">
                  <Clock className="w-3 h-3 mr-1" />
                  {taak.tijd}
                </span>
              )}
              <Badge className={`text-xs ${freqConfig.color}`}>
                {freqConfig.label}
              </Badge>
              {taak.categorie && (
                <Badge variant="outline" className="text-xs hidden sm:inline-flex">
                  {taak.categorie}
                </Badge>
              )}
            </div>
          </div>

          {/* External Link - Large touch target */}
          {taak.isExtern && taak.externUrl && (
            <a
              href={taak.externUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="p-3 sm:p-2 rounded-lg bg-slate-100 hover:bg-slate-200 active:bg-slate-300 text-slate-600 transition-colors shrink-0 min-w-[44px] min-h-[44px] flex items-center justify-center"
              title="Openen in nieuw tabblad"
            >
              <ExternalLink className="w-5 h-5" />
            </a>
          )}

          {/* Arrow - hidden on mobile */}
          <ChevronRight className="w-5 h-5 text-slate-300 hidden sm:block" />
        </div>
      </CardContent>
    </Card>
  );
};

export default Werkprogramma;
